import React, { useEffect, useRef, useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Modal,
  ScrollView,
  Linking,
  Platform,
  Animated,
  Easing,
} from 'react-native';
import { Image } from 'expo-image';
import { useTheme } from '../context/ThemeContext';
import { getApiUrl } from '../utils/apiConfig';

const SYSTEM_NOTIFICATION_IMAGE = require('../../assets/463862267_27497196046561095_1649697228244680471_n.jpg');

const EXERCISE_ASSIGNED_LINK = 'exercise-assigned';

function parseExerciseAssignedNotification(text) {
  const lines = String(text || '')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
  const bullets = lines.filter((line) => line.startsWith('• ')).map((line) => line.slice(2));
  const header = lines.filter((line) => !line.startsWith('• ')).join('\n');
  return { header, bullets };
}

function NotificationBody({ notification, styles, isUnread }) {
  const link = notification?.link || '';
  const textStyle = [
    styles.notificationText,
    isUnread ? styles.notificationTextUnread : styles.notificationTextRead,
  ];

  if (link === EXERCISE_ASSIGNED_LINK) {
    const { header, bullets } = parseExerciseAssignedNotification(notification.text);
    const useTwoColumns = bullets.length > 3;

    return (
      <View style={styles.notificationBody}>
        {header ? (
          <Text
            style={textStyle}
            {...(Platform.OS === 'web' ? { dataSet: { notificationExerciseHeader: 'true' } } : {})}
          >
            {header}
          </Text>
        ) : null}
        {bullets.length > 0 ? (
          <View
            style={[
              styles.exerciseListGrid,
              useTwoColumns ? styles.exerciseListGridTwoCol : null,
            ]}
            {...(Platform.OS === 'web' ? { dataSet: { notificationExerciseList: 'true' } } : {})}
          >
            {bullets.map((name, index) => (
              <View
                key={`${name}-${index}`}
                style={[
                  styles.exerciseListItem,
                  useTwoColumns ? styles.exerciseListItemHalf : null,
                ]}
              >
                <Text
                  style={[
                    styles.exerciseListItemText,
                    isUnread ? styles.notificationTextUnread : styles.notificationTextRead,
                  ]}
                  {...(Platform.OS === 'web' ? { dataSet: { notificationExerciseListText: 'true' } } : {})}
                >
                  • {name}
                </Text>
              </View>
            ))}
          </View>
        ) : null}
      </View>
    );
  }

  return (
    <Text style={textStyle}>
      {notification.text}
    </Text>
  );
}

export default function NotificationsBell({ navigation, user }) {
  const theme = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [failedSystemImageById, setFailedSystemImageById] = useState({});
  const [isVisible, setIsVisible] = useState(false);
  const slideAnim = useRef(new Animated.Value(0)).current;

  const fetchNotifications = async ({ reset }) => {
    if (!user?.id) return;

    const fetchLimit = reset ? 6 : 5;
    const fetchOffset = reset ? 0 : offset;

    if (reset) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }

    try {
      const response = await fetch(
        `${getApiUrl()}/api/notifications?user_id=${user.id}&limit=${fetchLimit}&offset=${fetchOffset}`
      );
      if (!response.ok) throw new Error('Failed to fetch notifications');
      const data = await response.json();
      const records = Array.isArray(data) ? data : [];

      if (reset) {
        setNotifications(records);
        setOffset(records.length);
      } else {
        setNotifications((prev) => [...prev, ...records]);
        setOffset((prev) => prev + records.length);
      }

      setHasMore(records.length === fetchLimit);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      if (reset) {
        setNotifications([]);
      }
      setHasMore(false);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    if (isVisible) {
      Animated.timing(slideAnim, {
        toValue: 1,
        duration: 220,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: true,
      }).start();
    }
  }, [isVisible, slideAnim]);

  const closePanel = () => {
    Animated.timing(slideAnim, {
      toValue: 0,
      duration: 180,
      easing: Easing.in(Easing.cubic),
      useNativeDriver: true,
    }).start(() => {
      setIsOpen(false);
      setIsVisible(false);
    });
  };

  const openPanel = async () => {
    setIsOpen(true);
    setIsVisible(true);
    slideAnim.setValue(0);
    await fetchNotifications({ reset: true });
  };

  const handleBellPress = async () => {
    if (isOpen) {
      closePanel();
    } else {
      await openPanel();
    }
  };

  const markNotificationRead = async (notificationId) => {
    try {
      await fetch(`${getApiUrl()}/api/notifications/${notificationId}/read`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: user?.id }),
      });
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const openNotificationLink = async (link) => {
    if (!link) return;

    try {
      if (/^https?:\/\//i.test(link)) {
        await Linking.openURL(link);
        return;
      }

      const chiComment = String(link).match(
        /^chi-gung-handbook\/(\d+)\/form\/([^/]+)\/comment\/(\d+)$/
      );
      if (chiComment && navigation?.navigate) {
        navigation.navigate('ChiGungHandbookFormComment', {
          userId: user?.id,
          user,
          courseId: chiComment[1],
          entrySlug: chiComment[2],
          commentId: chiComment[3],
          courseName: 'Chi Gung',
        });
        return;
      }

      const chi = String(link).match(/^chi-gung-handbook\/(\d+)\/form\/([^/]+)$/);
      if (chi && navigation?.navigate) {
        navigation.navigate('ChiGungHandbookForm', {
          userId: user?.id,
          user,
          courseId: chi[1],
          entrySlug: chi[2],
          courseName: 'Chi Gung',
        });
        return;
      }

      const snComment = String(link).match(/^student-notes\/(\d+)\/comment\/(\d+)$/);
      if (snComment && navigation?.navigate) {
        navigation.navigate('StudentNotes', {
          userId: user?.id,
          user,
          noteId: snComment[1],
          commentId: snComment[2],
        });
        return;
      }

      const snNote = String(link).match(/^student-notes\/(\d+)$/);
      if (snNote && navigation?.navigate) {
        navigation.navigate('StudentNotes', {
          userId: user?.id,
          user,
          noteId: snNote[1],
        });
        return;
      }

      const routineShared = String(link).match(/^routine-builder\/routine\/(\d+)$/);
      if (routineShared && navigation?.navigate) {
        navigation.navigate('RoutineBuilder', {
          userId: user?.id,
          user,
          highlightRoutineId: Number(routineShared[1]),
        });
        return;
      }

      if (link === EXERCISE_ASSIGNED_LINK) {
        return;
      }

      if (navigation?.navigate) {
        const routeName = link.replace(/^\//, '');
        navigation.navigate(routeName, { userId: user?.id, user });
      }
    } catch (error) {
      console.error('Error opening notification link:', error);
    }
  };

  const handleNotificationPress = async (notification) => {
    if (!notification?.id) return;

    if (!notification.is_read) {
      await markNotificationRead(notification.id);
      setNotifications((prev) =>
        prev.map((item) =>
          item.id === notification.id ? { ...item, is_read: true } : item
        )
      );
    }

    await openNotificationLink(notification.link);
    closePanel();
  };

  const styles = getStyles(theme);

  return (
    <>
      <TouchableOpacity style={styles.bellButton} onPress={handleBellPress} activeOpacity={0.7}>
        <Text style={styles.bellIcon}>🔔</Text>
      </TouchableOpacity>

      <Modal transparent visible={isOpen} animationType="none" onRequestClose={closePanel}>
        <View
          style={styles.overlay}
          {...(Platform.OS === 'web' ? { 'data-notification-element': true } : {})}
        >
          <TouchableOpacity
            style={styles.outsideTapArea}
            activeOpacity={1}
            onPress={closePanel}
            {...(Platform.OS === 'web' ? { 'data-notification-element': true } : {})}
          />
          <Animated.View
            style={[
              styles.panel,
              {
                transform: [
                  {
                    translateX: slideAnim.interpolate({
                      inputRange: [0, 1],
                      outputRange: [420, 0],
                    }),
                  },
                ],
              },
            ]}
            onStartShouldSetResponder={() => true}
            {...(Platform.OS === 'web' ? { 'data-notification-element': true } : {})}
          >
            <Text style={styles.panelTitle}>Notifications</Text>
            {loading ? (
              <Text style={styles.emptyText}>Loading...</Text>
            ) : notifications.length === 0 ? (
              <Text style={styles.emptyText}>No notifications</Text>
            ) : (
              <ScrollView style={styles.list} contentContainerStyle={styles.listContent}>
                {notifications.map((notification) => {
                  const isUnread = !notification.is_read;
                  const cardStyle = isUnread
                    ? styles.notificationUnread
                    : styles.notificationRead;

                  const hasCreator = Boolean(
                    notification.creator_profile_picture_url ||
                      notification.creator_name ||
                      notification.created_by
                  );

                  return (
                    <TouchableOpacity
                      key={notification.id}
                      style={[styles.notificationItem, cardStyle]}
                      onPress={() => handleNotificationPress(notification)}
                      activeOpacity={0.75}
                      {...(Platform.OS === 'web' ? { 'data-notification-element': true } : {})}
                    >
                      {notification.creator_profile_picture_url ? (
                        <Image
                          source={{ uri: notification.creator_profile_picture_url }}
                          style={styles.avatar}
                          contentFit="cover"
                        />
                      ) : hasCreator ? (
                        <View style={styles.defaultAvatar}>
                          <Text style={styles.defaultAvatarText}>👤</Text>
                        </View>
                      ) : (
                        failedSystemImageById[notification.id] ? (
                          <View style={styles.systemFallbackAvatar}>
                            <Text style={styles.systemFallbackAvatarText}>🔔</Text>
                          </View>
                        ) : (
                          <Image
                            source={SYSTEM_NOTIFICATION_IMAGE}
                            style={styles.avatar}
                            contentFit="cover"
                            onError={() =>
                              setFailedSystemImageById((prev) => ({
                                ...prev,
                                [notification.id]: true,
                              }))
                            }
                          />
                        )
                      )}
                      <NotificationBody
                        notification={notification}
                        styles={styles}
                        isUnread={isUnread}
                      />
                    </TouchableOpacity>
                  );
                })}

                {hasMore && (
                  <TouchableOpacity
                    style={styles.moreButton}
                    onPress={() => fetchNotifications({ reset: false })}
                    disabled={loadingMore}
                  >
                    <Text style={styles.moreButtonText}>
                      {loadingMore ? 'Loading...' : 'More'}
                    </Text>
                  </TouchableOpacity>
                )}
              </ScrollView>
            )}
          </Animated.View>
        </View>
      </Modal>
    </>
  );
}

const getStyles = (theme) =>
  StyleSheet.create({
    bellButton: {
      width: 40,
      height: 40,
      borderRadius: 20,
      alignItems: 'center',
      justifyContent: 'center',
      marginRight: 10,
      backgroundColor: theme.isDarkMode ? 'rgba(255,255,255,0.06)' : '#FFFFFF',
    },
    bellIcon: {
      fontSize: 20,
      color: theme.isDarkMode ? '#8a8a8a' : '#b8942f',
    },
    overlay: {
      flex: 1,
      backgroundColor: 'transparent',
      justifyContent: 'flex-start',
      alignItems: 'flex-end',
      paddingTop: Platform.OS === 'web' ? 72 : 62,
      paddingRight: 10,
      ...(Platform.OS === 'web' ? { overflow: 'hidden' } : {}),
    },
    outsideTapArea: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'transparent',
    },
    panel: {
      width: Platform.OS === 'web' ? 360 : '94%',
      maxHeight: '75%',
      borderRadius: 12,
      backgroundColor: theme.isDarkMode ? '#111111' : '#FFFFFF',
      borderWidth: 1,
      borderColor: theme.colors.border,
      padding: 12,
    },
    panelTitle: {
      fontSize: 16,
      fontWeight: '700',
      color: theme.colors.text,
      marginBottom: 10,
      backgroundColor: 'transparent',
    },
    list: {
      width: '100%',
    },
    listContent: {
      paddingBottom: 6,
    },
    notificationItem: {
      flexDirection: 'row',
      alignItems: 'center',
      borderRadius: 8,
      padding: 10,
      marginBottom: 8,
      borderWidth: 1,
      borderColor: theme.colors.border,
    },
    notificationUnread: {
      backgroundColor: '#d4af37',
    },
    notificationRead: {
      backgroundColor: theme.isDarkMode ? '#000000' : '#FFFFFF',
    },
    avatar: {
      width: 28,
      height: 28,
      borderRadius: 14,
      marginRight: 10,
      backgroundColor: 'transparent',
      borderWidth: 1,
      borderColor: theme.colors.border,
    },
    defaultAvatar: {
      width: 28,
      height: 28,
      borderRadius: 14,
      marginRight: 10,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: theme.isDarkMode ? '#2a2a2a' : '#e8e8e8',
    },
    defaultAvatarText: {
      fontSize: 14,
    },
    systemFallbackAvatar: {
      width: 28,
      height: 28,
      borderRadius: 14,
      marginRight: 10,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#d4af37',
      borderWidth: 1,
      borderColor: theme.colors.border,
    },
    systemFallbackAvatarText: {
      fontSize: 14,
      color: '#000000',
    },
    notificationText: {
      flex: 1,
      fontSize: 13,
      fontWeight: '700',
      borderRadius: 6,
      paddingVertical: 6,
      paddingHorizontal: 8,
      backgroundColor: 'transparent',
    },
    notificationBody: {
      flex: 1,
      backgroundColor: 'transparent',
    },
    exerciseListGrid: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      marginTop: 4,
      backgroundColor: 'transparent',
    },
    exerciseListGridTwoCol: {
      marginTop: 6,
    },
    exerciseListItem: {
      width: '100%',
      paddingVertical: 2,
      paddingHorizontal: 8,
      backgroundColor: 'transparent',
    },
    exerciseListItemHalf: {
      width: '50%',
    },
    exerciseListItemText: {
      fontSize: 12,
      fontWeight: '600',
      backgroundColor: 'transparent',
    },
    notificationTextUnread: {
      color: '#000000',
      backgroundColor: '#d4af37',
    },
    notificationTextRead: {
      color: theme.isDarkMode ? '#FFFFFF' : '#000000',
      backgroundColor: theme.isDarkMode ? '#000000' : '#FFFFFF',
    },
    moreButton: {
      paddingVertical: 10,
      borderRadius: 8,
      backgroundColor: theme.colors.primary || '#d4af37',
      alignItems: 'center',
      marginTop: 4,
    },
    moreButtonText: {
      color: '#FFFFFF',
      fontSize: 13,
      fontWeight: '700',
    },
    emptyText: {
      paddingVertical: 16,
      textAlign: 'center',
      color: theme.colors.text,
      opacity: 0.75,
      backgroundColor: 'transparent',
    },
  });
